import { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft, ArrowUpRight, Check, ChevronRight, History, Monitor,
  MousePointer2, Palette, Save, Send, SlidersHorizontal, Sparkles, Type, WandSparkles,
} from "lucide-react";
import { siteHost, siteUrl } from "../../lib/domains";
import DeleteSiteButton from "../../components/DeleteSiteButton";
import { useAppDialog } from "../../components/AppDialog";
import { samePage } from "./preview-bridge";
import { ASSISTANT_ENABLED, ASSISTANT_UNAVAILABLE_TITLE } from "./assistantAvailability";
import { HistoryPanel, InspectPanel, MotionPanel, ObjectBranch, StylesPanel } from "./panels";
import AddPage from "./AddPage";
import AssistantUnavailable from "./AssistantUnavailable";
import { useSiteEditor } from "./hooks/useSiteEditor";

export default function SiteEditor() {
  const ed = useSiteEditor();
  const navigate = useNavigate();
  const dialog = useAppDialog();
  const [expandedObjects, setExpandedObjects] = useState<Set<string>>(new Set());
  const composerRef = useRef<HTMLTextAreaElement | null>(null);
  const {
    site, head, error, notice, conflict, dirty, busy, saveProgress,
    mode, setMode, railTab, setRailTab, inspectorTab, setInspectorTab,
    currentPage, setCurrentPage, selectedPath, setSelectedPath,
    values, attributes, imageUpload, styles, recentStyles,
    assistantMessage, setAssistantMessage, assistantLog, activeJob,
    motionPaused, setMotionPaused, advancedMotion, setAdvancedMotion,
    frameRef, frameKey, setFrameKey, buildId, setBuildId, ready, setReady, frameTimedOut, prepareAttempted, manifest,
    previewUrl, publicPreviewUrl, previewOrigin, selectedField, selectedObject, pageObjects,
    model, timeline, contrastError, uploadAbort, styleCapabilities,
    prepare, sendHandshake, selectField, changeField, uploadImage, changeAttribute,
    setStyle, applyStyleSet, undoStyle, redoStyle, styleUndo, styleRedo,
    save, publish, askAssistant, requestNewPage, revert, labelVersion, runOperation, resolveConflict,
    deletedElements, undoDelete,
    setNotice, setError, pageLabel, postPreview,
    pageHint, setPageHint,
  } = ed;
  const modKey = /Mac|iP(hone|ad|od)/.test(navigator.platform ?? "") ? "⌘" : "Ctrl";
  // Panels offer "Ask ProtoPal" as the way to make changes the tactile controls
  // can't express — but only while that engine is actually reachable.
  const askAssistantCta = ASSISTANT_ENABLED
    ? () => { setMode("assistant"); composerRef.current?.focus(); }
    : undefined;

  if (error && !site) return <div className="editor-fatal">{error}</div>;
  if (!site || !head) return <div className="editor-loading">Preparing your editor…</div>;

  return (
    <div className={`site-editor mode-${mode}`}>
      <header className="editor-topbar">
        <Link to="/dashboard" className="editor-back" onClick={(event) => {
          if (!dirty) return;
          event.preventDefault();
          void dialog.confirm({
            title: "Leave without saving?",
            body: "Your unsaved changes to this site will be discarded.",
            confirmLabel: "Discard and leave",
            cancelLabel: "Stay here",
            tone: "danger",
          }).then((leave) => { if (leave) navigate("/dashboard"); });
        }}><ArrowLeft size={17} /><span>Back</span></Link>
        <div className="editor-site-title">
          <strong>{siteHost(site.subdomain)}</strong>
          <span aria-live="polite">{busy === "save" || busy === "revert" ? saveProgress : dirty ? <><i className="unsaved-dot" /> Unsaved changes</> : <><Check size={12} /> Draft saved</>}</span>
        </div>
        <div className="editor-mode-switch" role="tablist" aria-label="Editing mode">
          <button role="tab" aria-selected={mode === "direct"} className={mode === "direct" ? "active" : ""} onClick={() => setMode("direct")}><MousePointer2 size={16} /><span><b>Edit directly</b><small>Hands-on controls</small></span></button>
          <button
            role="tab"
            aria-selected={mode === "assistant"}
            className={mode === "assistant" ? "active" : ""}
            disabled={!ASSISTANT_ENABLED}
            title={ASSISTANT_ENABLED ? undefined : ASSISTANT_UNAVAILABLE_TITLE}
            onClick={() => setMode("assistant")}
          ><WandSparkles size={16} /><span><b>Ask ProtoPal</b><small>{ASSISTANT_ENABLED ? "Describe a change" : "Temporarily unavailable"}</small></span></button>
        </div>
        <div className="editor-top-actions">
          {publicPreviewUrl && <a href={publicPreviewUrl} target="_blank" rel="noreferrer" className="editor-button"><Monitor size={15} /><span>Preview</span></a>}
          {site.status === "active" && <a href={siteUrl(site.subdomain)} target="_blank" rel="noreferrer" className="editor-button"><span>Live</span><ArrowUpRight size={14} /></a>}
          <DeleteSiteButton site={site} className="editor-button" />
          <button className="editor-button" title="Save your edits to a draft preview. Publish when you’re ready to update the live site." disabled={!dirty || !!busy || !!contrastError} onClick={() => void save()}><Save size={15} /><span>{busy === "save" ? saveProgress ?? "Saving…" : "Save draft"}</span></button>
          <button className="editor-button primary" title={dirty ? "Save your changes before publishing." : "Update your live website with the latest saved draft."} disabled={!site.previewBuildId || !!busy || dirty || !!activeJob} onClick={() => void publish()}>{busy === "publish" ? "Publishing…" : "Publish latest draft"}</button>
        </div>
      </header>

      <div className="editor-workspace">
        <aside className="editor-rail" aria-label="Site structure">
          <div className="editor-rail-tabs" role="tablist">
            <button role="tab" aria-selected={railTab === "pages"} className={railTab === "pages" ? "active" : ""} onClick={() => setRailTab("pages")}>Pages</button>
            <button role="tab" aria-selected={railTab === "objects"} className={railTab === "objects" ? "active" : ""} onClick={() => setRailTab("objects")}>Objects</button>
          </div>
          <div className="editor-rail-body">
            {railTab === "pages" ? <>
              {model.pages.map((page) => (
              <button key={page.id} aria-label={page.label || pageLabel(page.path)} title={page.label || pageLabel(page.path)} className={`editor-page${samePage(currentPage, page.path) || samePage(currentPage, page.file) ? " active" : ""}`} onClick={() => {
                if (samePage(currentPage, page.path) || samePage(currentPage, page.file)) return;
                setCurrentPage(page.path);
                setSelectedPath(null);
                setReady(false);
                setFrameKey((value) => value + 1);
              }}>
                <span className="page-icon" aria-hidden="true">{(page.label || pageLabel(page.path)).slice(0, 1)}</span><span>{page.label || pageLabel(page.path)}</span><ChevronRight size={14} aria-hidden="true" />
              </button>
              ))}
              <AddPage
                disabled={!!busy || !!activeJob}
                blockedReason={activeJob ? "A change is already building" : undefined}
                onSubmit={(name, purpose) => void requestNewPage(name, purpose)}
              />
            </> : (
              <div className="object-tree">
                <div className="object-tree-page"><span className="page-icon">{pageLabel(currentPage).slice(0, 1)}</span><strong>{pageLabel(currentPage)}</strong></div>
                {pageObjects.length ? pageObjects.map((object, index) => <ObjectBranch key={`${currentPage}/${object.id}/${index}`} object={object} selectedPath={selectedPath} expanded={expandedObjects} onToggle={(id) => {
                  setExpandedObjects((current) => {
                    const next = new Set(current);
                    if (next.has(id)) next.delete(id);
                    else next.add(id);
                    return next;
                  });
                }} onSelect={(item) => {
                  if (item.fieldPath) return selectField(item.fieldPath);
                  setSelectedPath(item.id);
                  setInspectorTab("inspect");
                  postPreview( {
                    type: item.kind === "section" ? "focus-section" : item.kind === "page" ? "focus-page" : "focus-object",
                    ...(item.kind === "section" ? { sectionId: item.id } : item.kind === "page" ? { pageId: item.id.replace(/^page:/, "") } : { objectId: item.id }),
                  });
                }} />) : <p className="rail-empty">No semantic objects were provided for this page.</p>}
              </div>
            )}
          </div>
          <div className="editor-rail-foot"><Sparkles size={14} /><span>Edit your pages. Save your draft. Publish when ready.</span></div>
        </aside>

        <main className={`editor-canvas-wrap${conflict ? " has-conflict" : ""}`}>
          {notice && <div className="editor-toast" role="status">{notice}{deletedElements.length > 0 && <button className="editor-toast-undo" onClick={undoDelete}>Undo</button>}<button onClick={() => setNotice(null)} aria-label="Dismiss">×</button></div>}
          {error && <div className="editor-toast error" role="alert">{error}<button onClick={() => setError(null)} aria-label="Dismiss">×</button></div>}
          {pageHint && (
            <div className="editor-toast hint" role="status">
              <span>Hold <kbd>{modKey}</kbd> and click to open <strong>{pageHint.label}</strong> in the editor.</span>
              <button onClick={() => setPageHint(null)} aria-label="Dismiss">×</button>
            </div>
          )}
          {conflict && <div className="editor-conflict" role="alertdialog" aria-label="Edit conflict">
            <strong>Two versions need your choice</strong>
            <span>Your local edits and server version {conflict.server.version} are both preserved.</span>
            <button onClick={() => resolveConflict(true)}>Keep my edits</button>
            <button onClick={() => resolveConflict(false)}>Reload server version</button>
          </div>}
          {selectedField && !conflict && <button className="canvas-selection-chip" onClick={() => setMode("direct")}><MousePointer2 size={13} /><span>{selectedField.label}</span><small>{mode === "assistant" ? "Attached to prompt" : "Selected"}</small></button>}
          {!buildId ? (
            <div className="editor-no-preview"><Sparkles size={28} /><h2>Build this site before editing it.</h2><Link to={`/sites/${site.id}`} className="btn primary">Return to site</Link></div>
          ) : (
            <div className={`editor-browser${motionPaused ? " motion-paused" : ""}`}>
              <div className="editor-browser-chrome"><i /><i /><i /><span>{pageLabel(currentPage)} · Draft preview</span></div>
              {/* No allow-same-origin: the framed bundle is untrusted (a hostile
                  customer site could carry script), and same-origin + scripts on
                  a same-origin-served frame would let it reach this app's token.
                  Sandboxed to an opaque origin, its script can't touch the parent;
                  the editor bridge talks over postMessage, which still works. */}
              {previewUrl && <iframe key={`${buildId}-${frameKey}-${currentPage}-${manifest?.schemaVersion ?? "loading"}`} ref={frameRef} src={previewUrl} title={`Editing ${pageLabel(currentPage)} on ${siteHost(site.subdomain)}`} sandbox="allow-scripts allow-forms allow-popups" onLoad={sendHandshake} />}
              {!ready && (
                <div className="editor-frame-loading">
                  {!frameTimedOut && <div className="builder-spinner" />}
                  <span>
                    {frameTimedOut
                      ? "The uploaded preview did not connect to the editor."
                      : busy === "prepare" ? "Making this site editable…" : "Connecting to your site…"}
                  </span>
                  {frameTimedOut && publicPreviewUrl && (
                    <a className="editor-button" href={publicPreviewUrl} target="_blank" rel="noreferrer">
                      Open direct preview
                    </a>
                  )}
                  <button className="editor-button" onClick={() => {
                    prepareAttempted.current = false;
                    setReady(false);
                    setFrameKey((value) => value + 1);
                    void prepare();
                  }}>
                    Retry editor
                  </button>
                </div>
              )}
            </div>
          )}
        </main>

        <aside className="editor-inspector" aria-label={mode === "assistant" ? "ProtoPal assistant" : "Editor inspector"}>
          {mode === "assistant" && !ASSISTANT_ENABLED ? (
            <AssistantUnavailable onEditDirectly={() => setMode("direct")} />
          ) : mode === "assistant" ? (
            <div className="assistant-panel">
              <div className="assistant-head"><span><Sparkles size={16} /> ProtoPal</span><small>{activeJob ? "Working in the background" : "Ready"}</small></div>
              {selectedField && <button className="assistant-selection" onClick={() => setMode("direct")}><MousePointer2 size={13} /><span><small>Editing context</small><strong>{selectedField.label}</strong></span><ChevronRight size={14} /></button>}
              <div className="assistant-thread" aria-live="polite">
                {assistantLog.map((entry) => <div key={entry.id} className={`assistant-bubble ${entry.role}`}><p>{entry.text}</p>{entry.status && !["ready", "ready_unverified", "failed", "runner_start_failed", "timeout", "cancelled"].includes(entry.status) && <div className="assistant-progress"><span /></div>}</div>)}
              </div>
              <div className="assistant-suggestions">
                {["Refine this copy", "Make this section clearer", "Improve the mobile layout"].map((suggestion) => <button key={suggestion} onClick={() => { setAssistantMessage(suggestion); composerRef.current?.focus(); }}>{suggestion}</button>)}
              </div>
              <div className="assistant-composer">
                <textarea
                  ref={composerRef}
                  value={assistantMessage}
                  disabled={!!activeJob}
                  onChange={(event) => setAssistantMessage(event.target.value)}
                  onKeyDown={(event) => {
                    // Enter used to bypass the send button's disabled state, so
                    // a second request could start over a running build.
                    if (event.key !== "Enter" || event.shiftKey) return;
                    event.preventDefault();
                    if (busy || activeJob) return;
                    void askAssistant();
                  }}
                  placeholder={activeJob
                    ? "ProtoPal is working on your last change…"
                    : selectedField ? `Ask about ${selectedField.label}…` : "Describe the change you want…"}
                />
                <button
                  disabled={!assistantMessage.trim() || !!busy || !!activeJob}
                  title={activeJob ? "A change is already building" : undefined}
                  onClick={() => void askAssistant()}
                  aria-label="Send request"
                ><Send size={16} /></button>
              </div>
            </div>
          ) : (
            <>
              <div className="editor-inspector-tabs" role="tablist">
                {([
                  ["inspect", Type, "Elements"], ["styles", Palette, "Styles"], ["motion", SlidersHorizontal, "Motion"], ["history", History, "History"],
                ] as const).map(([tab, Icon, label]) => <button key={tab} role="tab" aria-selected={inspectorTab === tab} className={inspectorTab === tab ? "active" : ""} onClick={() => setInspectorTab(tab)}><Icon size={15} /><span>{label}</span></button>)}
              </div>
              <div className="editor-inspector-body">
                {inspectorTab === "inspect" && <InspectPanel field={selectedField} object={selectedObject} value={selectedPath ? values[selectedPath] ?? "" : ""} attributes={selectedPath ? attributes[selectedPath] : undefined} upload={imageUpload} allowFocal={ed.canEditImageFocal} allowImageUrl={manifest?.schemaVersion !== 3} onUpload={(file) => selectedPath && void uploadImage(selectedPath, file)} onCancelUpload={() => uploadAbort.current?.abort()} onChange={(value) => selectedPath && changeField(selectedPath, value)} onMeta={(key, value) => selectedPath && changeAttribute(selectedPath, key, value)} onOperation={runOperation} />}
                {inspectorTab === "styles" && <StylesPanel styles={styles} recent={recentStyles} contrastError={contrastError} capabilities={styleCapabilities} onStyle={setStyle} onApply={applyStyleSet} onUndo={undoStyle} onRedo={redoStyle} canUndo={styleUndo.current.length > 0} canRedo={styleRedo.current.length > 0} onAskAssistant={askAssistantCta} />}
                {inspectorTab === "motion" && <MotionPanel styles={styles} paused={motionPaused} advanced={advancedMotion} editable={styleCapabilities.motion} onPause={() => { setMotionPaused((value) => !value); postPreview( { type: "pause-motion", paused: !motionPaused }); }} onAdvanced={() => setAdvancedMotion((value) => !value)} onStyle={setStyle} onAskAssistant={askAssistantCta} />}
                {inspectorTab === "history" && <HistoryPanel timeline={timeline} headVersion={head.version} liveBuildId={site.liveBuildId} previewBuildId={site.previewBuildId} busy={!!busy} unsavedChanges={dirty} onLabel={labelVersion} onPreview={(id) => { setBuildId(id); setReady(false); setNotice("Previewing an earlier version. Restore it to edit from here."); }} onRevert={revert} onPublish={publish} />}
              </div>
            </>
          )}
        </aside>
      </div>
    </div>
  );
}
